/**
 * Custom Image Slider Web Component
 * Supports: autoplay, mouse drag, touch swipe, dot/arrow navigation, looping
 */
class CustomImageSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = null;
    this.autoplayTimer = null;
    this.progressTimer = null;
    this.isPaused = false;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.slides = this.querySelectorAll('[data-slider-slide]');
    this.dotsContainer = this.querySelector('[data-slider-dots]');
    this.prevBtn = this.querySelector('[data-slider-prev]');
    this.nextBtn = this.querySelector('[data-slider-next]');
    this.progressBar = this.querySelector('[data-slider-progress]');

    if (!this.track || this.slides.length === 0) return;

    this.slideCount = this.slides.length;
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = parseInt(this.dataset.speed, 10) || 5000;
    this.loop = true;

    this.buildDots();
    this.bindEvents();
    this.goToSlide(0, false);

    if (this.autoplay && this.slideCount > 1) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.slideCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'custom-slider__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        this.goToSlide(i);
        this.resetAutoplay();
      });
      this.dotsContainer.appendChild(dot);
    }
    this.dots = this.dotsContainer.querySelectorAll('.custom-slider__dot');
  }

  bindEvents() {
    // Arrow buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.prev();
        this.resetAutoplay();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.next();
        this.resetAutoplay();
      });
    }

    // Mouse drag
    this.track.addEventListener('mousedown', this.onDragStart.bind(this));
    this.track.addEventListener('mousemove', this.onDragMove.bind(this));
    this.track.addEventListener('mouseup', this.onDragEnd.bind(this));
    this.track.addEventListener('mouseleave', this.onDragEnd.bind(this));

    // Touch events
    this.track.addEventListener('touchstart', this.onDragStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.onDragMove.bind(this), { passive: false });
    this.track.addEventListener('touchend', this.onDragEnd.bind(this));

    // Prevent image drag ghost
    this.track.querySelectorAll('img').forEach((img) => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });

    // Pause autoplay on hover/focus
    this.addEventListener('mouseenter', () => {
      if (this.autoplay) this.pauseAutoplay();
    });
    this.addEventListener('mouseleave', () => {
      if (this.autoplay && !this.isPaused) this.startAutoplay();
    });
    this.addEventListener('focusin', () => {
      if (this.autoplay) this.pauseAutoplay();
    });
    this.addEventListener('focusout', () => {
      if (this.autoplay && !this.isPaused) this.startAutoplay();
    });

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
        this.resetAutoplay();
      } else if (e.key === 'ArrowRight') {
        this.next();
        this.resetAutoplay();
      }
    });
  }

  getSlideWidth() {
    return this.slides[0].offsetWidth;
  }

  onDragStart(e) {
    if (this.slideCount <= 1) return;
    this.isDragging = true;
    this.track.classList.add('is-dragging');
    this.startX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    this.prevTranslate = this.currentTranslate;
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    const currentX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const diff = currentX - this.startX;

    // Prevent scroll while swiping horizontally
    if (e.type === 'touchmove' && Math.abs(diff) > 10) {
      e.preventDefault();
    }

    this.currentTranslate = this.prevTranslate + diff;
    this.setTrackPosition(this.currentTranslate);
  }

  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.classList.remove('is-dragging');

    const movedBy = this.currentTranslate - this.prevTranslate;
    const threshold = this.getSlideWidth() * 0.2;

    if (movedBy < -threshold) {
      this.next();
    } else if (movedBy > threshold) {
      this.prev();
    } else {
      this.goToSlide(this.currentIndex);
    }

    this.resetAutoplay();
  }

  setTrackPosition(position) {
    this.track.style.transform = `translateX(${position}px)`;
  }

  goToSlide(index, animate = true) {
    if (index < 0) {
      index = this.slideCount - 1;
    } else if (index >= this.slideCount) {
      index = 0;
    }

    this.currentIndex = index;
    this.currentTranslate = -index * this.getSlideWidth();

    if (!animate) {
      this.track.style.transition = 'none';
    }

    this.setTrackPosition(this.currentTranslate);

    if (!animate) {
      // Force reflow then restore transition
      this.track.offsetHeight;
      this.track.style.transition = '';
    }

    this.updateDots();
    this.updateAriaStates();
  }

  next() {
    this.goToSlide(this.currentIndex + 1);
  }

  prev() {
    this.goToSlide(this.currentIndex - 1);
  }

  updateDots() {
    if (!this.dots) return;
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
    });
  }

  updateAriaStates() {
    this.slides.forEach((slide, i) => {
      const isActive = i === this.currentIndex;
      slide.setAttribute('aria-hidden', !isActive);
      // Allow tab focus only on active slide content
      const focusable = slide.querySelectorAll('a, button');
      focusable.forEach((el) => {
        el.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.slideCount <= 1) return;

    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.speed);

    // Progress bar
    if (this.progressBar) {
      this.startProgress();
    }
  }

  pauseAutoplay() {
    this.stopAutoplay();
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    if (this.progressBar) {
      this.progressBar.style.width = '0%';
      this.progressBar.style.transition = 'none';
    }
  }

  resetAutoplay() {
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  startProgress() {
    if (!this.progressBar) return;
    // Reset
    this.progressBar.style.transition = 'none';
    this.progressBar.style.width = '0%';
    // Force reflow
    this.progressBar.offsetHeight;
    // Animate
    this.progressBar.style.transition = `width ${this.speed}ms linear`;
    this.progressBar.style.width = '100%';
  }
}

customElements.define('custom-image-slider', CustomImageSlider);
